export type RunyiStatusTone =
  | "success"
  | "processing"
  | "live"
  | "ai"
  | "action"
  | "warning"
  | "danger"
  | "neutral";

export type RunyiStatusPresentation = {
  raw: string;
  token: string;
  canonicalToken: string;
  label: string;
  tone: RunyiStatusTone;
  recognized: boolean;
};

const SUCCESS = new Set([
  "ACTIVE",
  "ACTIVE_HEALTHY",
  "CONNECTED",
  "HEALTHY",
  "ONLINE",
  "READY",
  "VERIFIED",
  "AVAILABLE",
  "SUCCESS",
  "SUCCEEDED",
  "COMPLETE",
  "COMPLETED",
  "ENABLED",
  "INSTALLED",
  "CURRENT",
  "PASS",
  "PASSED",
  "OPERATIONAL",
  "AUTHORIZED",
  "ENROLLED",
  "BOUND",
  "SYNCED",
  "DEPLOYED",
  "PUBLISHED",
  "RESOLVED",
  "IN_SYNC",
  "FRESH",
  "CANONICAL",
  "EXECUTABLE",
  "COMPOSED",
  "REUSE",
  "POOL",
  "INSTALLABLE",
  "SUPPORTED",
  "CONFIRMED",
  "VALID",
  "FULL",
  "SATISFIED",
  "MARKETPLACE_READY",
]);

const PROCESSING = new Set([
  "RUNNING",
  "PROCESSING",
  "WORKING",
  "BACKEND_WORKING",
  "PREPARING",
  "PROVISIONING",
  "PROVISION",
  "IN_PROGRESS",
  "RUNNING_PENDING",
  "RETRY",
  "RETRYING",
  "VERIFYING",
  "RECONNECTING",
  "APPLYING",
  "CREATING",
  "LOADING",
  "CONFIGURING",
  "DEPLOYING",
  "SYNCING",
  "QUEUED",
  "STARTING",
  "DISCOVERING",
  "RESOLVING",
  "REGISTERING",
  "BACKEND_DEFINITION_PENDING",
  "BACKEND_DEFINITION_READY",
  "ADAPTER_PENDING",
  "ADAPTER_READY",
]);

const LIVE = new Set([
  "LIVE",
  "REALTIME",
  "STREAMING",
  "LIVE_DATA",
  "NETWORK_CONNECTED",
  "PROVIDER_LINK",
  "PROVIDER_LINKED",
  "PROVIDER_LINK_CONNECTED",
  "EXTERNAL_CONNECTED",
  "EXTERNAL_CONNECTION_ACTIVE",
  "EVENT_STREAM_ACTIVE",
  "REALTIME_CONNECTED",
]);

const AI = new Set([
  "AI",
  "AI_ACTIVE",
  "AI_READY",
  "AI_WORKING",
  "AI_RUNNING",
  "AUTOMATION",
  "AUTOMATION_ACTIVE",
  "AUTOMATION_RUNNING",
  "AGENT",
  "AGENT_ACTIVE",
  "AGENT_RUNNING",
  "INTELLIGENCE_ACTION",
]);

const ACTION = new Set([
  "ACTION_REQUIRED",
  "AUTH_REQUIRED",
  "AUTHORIZATION_REQUIRED",
  "CREDENTIAL_REQUIRED",
  "PERMISSION_REQUIRED",
  "TOKEN_REQUIRED",
  "MFA_REQUIRED",
  "CONSENT_REQUIRED",
  "INPUT_REQUIRED",
  "CONFIGURATION_REQUIRED",
  "SELECT_TARGET",
  "SELECT_ACCOUNT",
  "SELECT_PROJECT",
  "SIGN_IN",
  "OTP_PENDING",
  "REQUIRES_APPROVAL",
  "NEEDS_APPROVAL",
  "AWAITING_APPROVAL",
  "REQUIRES_CONFIRMATION",
  "ENROLLMENT",
]);

const WARNING = new Set([
  "PENDING",
  "DEGRADED",
  "WARNING",
  "WARN",
  "PARTIAL",
  "CHECK",
  "STALE",
  "WAITING",
  "SUSPENDED",
  "MISSING",
  "OPEN",
  "ACKNOWLEDGED",
  "INSUFFICIENT_SCOPE",
  "MIGRATION_REQUIRED",
  "PREPARABLE",
  "DESIRED",
  "APPROVED",
  "ASSIGNED",
  "UNRESOLVED",
  "FULLY_RESERVED",
  "PREVIEWED",
  "REQUIRED",
  "PHASE_2_NEEDED",
]);

const DANGER = new Set([
  "ERROR",
  "FAILED",
  "FAILURE",
  "OFFLINE",
  "DISCONNECTED",
  "BLOCKED",
  "DENIED",
  "REVOKED",
  "UNHEALTHY",
  "MISCONFIGURED",
  "EXPIRED",
  "CANCELLED",
  "CANCELED",
  "BROKEN",
  "TIMEOUT",
  "TIMED_OUT",
  "SETUP_FAILED",
  "CONNECTION_ERROR",
  "OUTAGE",
  "CRITICAL",
  "DRIFT",
  "QUARANTINED",
  "REJECTED",
  "EXHAUSTED",
  "INVALID",
]);

const NEUTRAL = new Set([
  "UNKNOWN",
  "NOT_CONFIGURED",
  "NOT_CONNECTED",
  "NOT_INSTALLED",
  "NOT_READY",
  "UNSUPPORTED",
  "INACTIVE",
  "DISABLED",
  "DRAFT",
  "CATALOGUED",
  "PLANNED",
  "NOT_AVAILABLE",
  "UNAVAILABLE",
  "DEFERRED",
  "READ_ONLY",
  "CONFIGURED",
  "RETIRED",
  "DISMISSED",
  "OBSERVED",
  "ESTIMATED",
  "NOT_APPLICABLE",
  "PROJECT_REGISTERED",
  "DISCOVERED",
  "SELECTED",
  "LIMITED",
  "DASHBOARD",
  "OWNER_GRADE_M4",
  "NONE",
]);

const AI_STATUS_PREFIXES = ["AI", "AGENT", "AUTOMATION", "INTELLIGENCE"] as const;
const LIVE_STATUS_PREFIXES = ["REALTIME", "NETWORK", "PROVIDER_LINK", "EXTERNAL_CONNECTION", "EVENT_STREAM"] as const;

const NAMESPACED_STATUS_PREFIXES = [
  "IDENTITY",
  "BACKEND",
  "RUNYI",
  "PROVIDER",
  "COST",
  "APPROVAL",
  "PREVIEW",
  "PROJECT",
  "TENANT",
  "CONNECTION",
  "DATA",
  "ONE_TIME",
  "M4",
] as const;

export function normalizeRunyiStatus(value: unknown) {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function directToneForCanonicalToken(token: string): RunyiStatusTone | null {
  if (DANGER.has(token)) return "danger";
  if (ACTION.has(token)) return "action";
  if (WARNING.has(token)) return "warning";
  if (PROCESSING.has(token)) return "processing";
  if (LIVE.has(token)) return "live";
  if (AI.has(token)) return "ai";
  if (SUCCESS.has(token)) return "success";
  if (NEUTRAL.has(token)) return "neutral";
  return null;
}

function toneForSemanticPrefix(token: string): RunyiStatusTone | null {
  for (const prefix of AI_STATUS_PREFIXES) {
    const marker = `${prefix}_`;
    if (!token.startsWith(marker)) continue;
    const candidate = token.slice(marker.length);
    const nested = directToneForCanonicalToken(candidate);
    if (nested === "danger" || nested === "action" || nested === "warning") return nested;
    return "ai";
  }

  for (const prefix of LIVE_STATUS_PREFIXES) {
    const marker = `${prefix}_`;
    if (!token.startsWith(marker)) continue;
    const candidate = token.slice(marker.length);
    const nested = directToneForCanonicalToken(candidate);
    if (nested === "danger" || nested === "action" || nested === "warning") return nested;
    return "live";
  }

  return null;
}

function toneForCanonicalToken(token: string): RunyiStatusTone | null {
  return directToneForCanonicalToken(token) ?? toneForSemanticPrefix(token);
}

function namespacedCanonicalToken(token: string) {
  for (const prefix of NAMESPACED_STATUS_PREFIXES) {
    const marker = `${prefix}_`;
    if (!token.startsWith(marker)) continue;
    const candidate = token.slice(marker.length);
    if (toneForCanonicalToken(candidate)) return candidate;
  }
  return null;
}

function healthSummaryPresentation(raw: string, token: string): RunyiStatusPresentation | null {
  const match = raw.match(/^\s*(\d+)\s*\/\s*(\d+)\s+healthy\s*$/i);
  if (!match) return null;
  const healthy = Number(match[1]);
  const total = Number(match[2]);
  const canonicalToken = total > 0 && healthy === total ? "HEALTHY" : total > 0 ? "DEGRADED" : "UNKNOWN";
  const tone = toneForCanonicalToken(canonicalToken) ?? "neutral";
  return { raw, token, canonicalToken, label: raw, tone, recognized: true };
}

export function resolveRunyiStatus(value: unknown): RunyiStatusPresentation {
  const raw = String(value ?? "").trim();
  const token = normalizeRunyiStatus(raw);

  const healthSummary = healthSummaryPresentation(raw, token);
  if (healthSummary) return healthSummary;

  const directTone = toneForCanonicalToken(token);
  if (directTone) {
    return {
      raw,
      token,
      canonicalToken: token,
      label: raw || token,
      tone: directTone,
      recognized: true,
    };
  }

  const canonicalToken = namespacedCanonicalToken(token);
  const namespacedTone = canonicalToken ? toneForCanonicalToken(canonicalToken) : null;
  if (canonicalToken && namespacedTone) {
    return {
      raw,
      token,
      canonicalToken,
      label: raw || token,
      tone: namespacedTone,
      recognized: true,
    };
  }

  return {
    raw,
    token,
    canonicalToken: token || "UNKNOWN",
    label: raw,
    tone: "neutral",
    recognized: false,
  };
}

export const runyiStatusToneClasses: Record<RunyiStatusTone, string> = {
  success: "border-lime-400/40 bg-lime-400/10 text-lime-700 dark:text-lime-300",
  processing: "border-blue-500/35 bg-blue-500/10 text-blue-700 dark:text-blue-300",
  live: "border-cyan-500/35 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
  ai: "border-violet-500/35 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  action: "border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  warning: "border-amber-500/35 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  danger: "border-red-500/35 bg-red-500/10 text-red-700 dark:text-red-300",
  neutral: "border-foreground/12 bg-foreground/[0.035] text-foreground/50",
};

export const runyiStatusDotClasses: Record<RunyiStatusTone, string> = {
  success: "bg-lime-400",
  processing: "bg-blue-500",
  live: "bg-cyan-400",
  ai: "bg-violet-500",
  action: "bg-orange-500",
  warning: "bg-amber-400",
  danger: "bg-red-500",
  neutral: "bg-foreground/35",
};

export const runyiStatusTextClasses: Record<RunyiStatusTone, string> = {
  success: "text-lime-700 dark:text-lime-300",
  processing: "text-blue-700 dark:text-blue-300",
  live: "text-cyan-700 dark:text-cyan-300",
  ai: "text-violet-700 dark:text-violet-300",
  action: "text-orange-700 dark:text-orange-300",
  warning: "text-amber-700 dark:text-amber-300",
  danger: "text-red-700 dark:text-red-300",
  neutral: "text-foreground/50",
};

export const runyiStatusSoftSurfaceClasses: Record<RunyiStatusTone, string> = {
  success: "bg-lime-400/10 text-lime-700 dark:text-lime-300",
  processing: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  live: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
  ai: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
  action: "bg-orange-500/10 text-orange-700 dark:text-orange-300",
  warning: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  danger: "bg-red-500/10 text-red-700 dark:text-red-300",
  neutral: "bg-foreground/[0.05] text-foreground/50",
};

export const runyiStatusRowClasses: Record<RunyiStatusTone, string> = {
  success: "border-lime-400/20 bg-lime-400/[0.05]",
  processing: "border-blue-400/20 bg-blue-500/[0.05]",
  live: "border-cyan-400/20 bg-cyan-500/[0.05]",
  ai: "border-violet-400/20 bg-violet-500/[0.05]",
  action: "border-orange-400/20 bg-orange-500/[0.05]",
  warning: "border-amber-400/20 bg-amber-500/[0.05]",
  danger: "border-red-400/20 bg-red-500/[0.05]",
  neutral: "border-foreground/8 bg-foreground/[0.02]",
};

export const runyiStatusProgressClasses: Record<RunyiStatusTone, string> = {
  success: "bg-lime-400",
  processing: "bg-blue-500",
  live: "bg-cyan-400",
  ai: "bg-violet-500",
  action: "bg-orange-500",
  warning: "bg-amber-400",
  danger: "bg-red-500",
  neutral: "bg-foreground/25",
};
